import React, { forwardRef, ForwardRefRenderFunction, useState, useRef, useEffect } from 'react'
import { 
  Box, 
  Input, 
  FormControl, 
  FormLabel, 
  FormErrorMessage, 
  Flex, 
  Tag, 
  TagLabel, 
  TagCloseButton,
  List,
  ListItem,
  Text,
  SelectProps
} from '@chakra-ui/react'

interface OptionsProps {
  value: string | number
  label: string
}

interface CustomProps extends SelectProps {
  name: string
  label?: string
  errors?: any
  options: OptionsProps[]
  labelStyle?: any
  placeholder?: string
  extraFunction: (selectedValues: string[]) => any
  onRemove?: (value: string | number) => any
  defaultValues?: string[]
}

const BaseSelect: ForwardRefRenderFunction<HTMLInputElement, CustomProps> = (
  { 
    name, 
    label, 
    errors = null, 
    extraFunction, 
    onRemove,
    options, 
    labelStyle, 
    placeholder = "Pesquisar...",
    defaultValues = [],
    ...rest 
  },
  ref
) => {
  const [searchValue, setSearchValue] = useState('')
  const [selectedTags, setSelectedTags] = useState<OptionsProps[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Filtrar opções baseado na busca e excluir as já selecionadas
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchValue.toLowerCase()) &&
    !selectedTags.some(tag => tag.value === option.value)
  )

  // Função para selecionar uma opção
  const handleSelectOption = (option: OptionsProps) => {
    const newTags = [...selectedTags, option]
    setSelectedTags(newTags)
    setSearchValue('')
    setIsOpen(false)
    
    // Passar array completo de valores selecionados
    const allSelectedValues = newTags.map(tag => tag.value.toString())
    extraFunction(allSelectedValues)
    
    // Focar novamente no input após seleção
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  // Função para remover uma tag
  const handleRemoveTag = (valueToRemove: string | number) => {
    const newTags = selectedTags.filter(tag => tag.value !== valueToRemove)
    setSelectedTags(newTags)
    
    // Chamar onRemove se fornecido
    if (onRemove) {
      onRemove(valueToRemove)
    }
    
    // Atualizar extraFunction com novo array de valores
    const allSelectedValues = newTags.map(tag => tag.value.toString())
    extraFunction(allSelectedValues)
  }

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Definir tags iniciais quando defaultValues mudar
  useEffect(() => {
    if (defaultValues && defaultValues.length > 0) {
      const initialTags = options.filter(option => defaultValues.includes(option.value.toString()));
      setSelectedTags(initialTags);
    } else {
      setSelectedTags([]);
    }
  }, [defaultValues, options]);

  return (
    <FormControl id={name} isInvalid={!(errors == null)}>
      {!!label && <FormLabel color="green.dark" style={labelStyle}>{label}</FormLabel>}
      
      <Box position="relative" ref={containerRef} width="100%">
        {/* Container do input com tags */}
        <Box
          border="1px"
          borderColor={isFocused ? "green.dark" : "green.dark"}
          borderRadius="md"
          minH="48px"
          bg="white"
          p={2}
          cursor="text"
          onClick={() => {
            inputRef.current?.focus()
            setIsOpen(true)
            setIsFocused(true)
          }}
        >
          <Flex wrap="wrap" gap={1} align="center">
            {/* Tags selecionadas */}
            {selectedTags.map((tag) => (
              <Tag
                key={tag.value}
                size="md"
                colorScheme="purple"
                variant="solid"
                bg="green.dark"
              >
                <TagLabel>{tag.label}</TagLabel>
                <TagCloseButton onClick={() => handleRemoveTag(tag.value)} />
              </Tag>
            ))}
            
            {/* Input de busca */}
            <Input
              ref={inputRef}
              variant="unstyled"
              placeholder={selectedTags.length === 0 ? placeholder : ""}
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value)
                setIsOpen(true)
              }}
              onFocus={() => {
                setIsOpen(true)
                setIsFocused(true)
              }}
              flex="1"
              minW="120px"
              color="green.dark"
              _placeholder={{ color: 'gray.400' }}
            />
          </Flex>
        </Box>

        {/* Dropdown com opções filtradas */}
        {isOpen && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            bg="white"
            border="1px"
            borderColor="green.dark"
            borderTop="none"
            borderRadius="0 0 md md"
            maxH="200px"
            overflowY="auto"
            zIndex={1000}
            boxShadow="md"
          >
            {filteredOptions.length > 0 ? (
              <List>
                {filteredOptions.map((option) => (
                  <ListItem
                    key={option.value}
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => handleSelectOption(option)}
                    borderBottom="1px"
                    borderColor="gray.100"
                    color="green.dark"
                  >
                    <Text>{option.label}</Text>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box p={3}>
                <Text color="gray.500" fontSize="sm">
                  {searchValue ? 'Nenhum resultado encontrado' : 'Digite para buscar...'}
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Box>
      
      {!(errors == null) && <FormErrorMessage>{errors.message}</FormErrorMessage>}
    </FormControl>
  )
}

export const SelectWithSearch = forwardRef(BaseSelect)
